import {useEffect, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Joyride, EVENTS, STATUS, EventData, Step} from 'react-joyride';
import {useLocation, useNavigate} from 'react-router-dom';
import toast from 'react-hot-toast';
import SplitPractice from './SplitPractice';
import {tourColors, tourStyles} from './tourTheme';
import useAuth from '../../../hooks/auth/useAuth';
import {get, post} from '../../../services/core';

export const welcomeSteps: Step[] = [
  {target: '[data-tour="scan"]', title: 'Start with Scan',
    content: 'Tap Scan to practise with a sample receipt. No camera, scan allowance or real receipts involved.'},
];

type TourStatus = {completed: boolean};
const endpoint = '/v1/users/me/tour';

function AccountTour({userId}: {userId: number}) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storageKey = `payme-joyride-v1:${userId}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === 'done'; } catch { return false; }
  });
  const [replay, setReplay] = useState(0);
  const [forced, setForced] = useState(false);
  const [active, setActive] = useState(false);
  const [practice, setPractice] = useState(false);
  const syncAttempted = useRef(false);
  const queryKey = ['welcomeTour', userId];
  const status = useQuery<TourStatus>(queryKey, () => get(endpoint), {retry: false, staleTime: Infinity});
  const completion = useMutation<TourStatus>(() => post(endpoint, {}), {
    onSuccess: data => queryClient.setQueryData(queryKey, data),
    onError: () => toast.error('Your tour preference could not sync. We’ll retry next time you open the app.'),
  });
  const {mutate} = completion;

  useEffect(() => {
    if (dismissed && status.data?.completed === false && !syncAttempted.current) {
      syncAttempted.current = true;
      mutate();
    }
  }, [dismissed, status.data?.completed, mutate]);

  useEffect(() => {
    const replayTour = () => {
      setReplay(value => value + 1);
      setForced(true);
      setActive(true);
      setPractice(false);
      navigate('/home');
    };
    window.addEventListener('payme:replay-tour', replayTour);
    return () => window.removeEventListener('payme:replay-tour', replayTour);
  }, [navigate]);

  useEffect(() => {
    if (location.pathname === '/home' && (forced || (!dismissed && status.data?.completed === false))) {
      setActive(true);
    }
  }, [location.pathname, forced, dismissed, status.data?.completed]);

  useEffect(() => {
    if (!active || practice || location.pathname !== '/home') return;
    const startPractice = (event: MouseEvent) => {
      if (!(event.target instanceof Element) || !event.target.closest('[data-tour="scan"], [aria-label="Scan or add a receipt"]')) return;
      event.preventDefault();
      event.stopPropagation();
      setPractice(true);
    };
    document.addEventListener('click', startPractice, true);
    return () => document.removeEventListener('click', startPractice, true);
  }, [active, practice, location.pathname]);

  const complete = () => {
    setPractice(false);
    setActive(false);
    setForced(false);
    setDismissed(true);
    try { localStorage.setItem(storageKey, 'done'); } catch {}
    if (!status.data?.completed) {
      syncAttempted.current = true;
      mutate();
    }
  };
  const finish = ({type, status: tourStatus}: EventData) => {
    if (type !== EVENTS.TOUR_END) return;
    if (tourStatus === STATUS.SKIPPED) complete();
    else if (tourStatus === STATUS.FINISHED) setPractice(true);
  };

  if (active && practice) return <SplitPractice key={replay} onExit={complete}
    onScan={() => {complete(); navigate('/home');}}
    onPlans={() => {complete(); navigate('/profile');}}/>;

  return <Joyride key={replay} steps={welcomeSteps}
    run={active && !practice && location.pathname === '/home'} continuous scrollToFirstStep onEvent={finish}
    floatingOptions={{strategy: 'fixed', autoUpdate: {ancestorScroll: true, elementResize: true}}}
    options={{skipBeacon: true, buttons: ['close'], showProgress: false, blockTargetInteraction: false, disableFocusTrap: true,
      overlayClickAction: false, closeButtonAction: 'skip', ...tourColors, width: 340, zIndex: 1600}}
    locale={{last: 'Try practice receipt', close: 'Skip guide'}}
    styles={tourStyles}/>;

}

export default function FirstUseTour() {
  const {user, isAuthenticated} = useAuth();
  return isAuthenticated && user?.id != null ? <AccountTour key={user.id} userId={user.id}/> : null;
}
